import { useState } from 'react';
import { IconKey, IconPlus } from '../components/icons';
import { CodeTabs, CopyButton, Empty, Field, Modal, Skeleton, sendSnippets, useAsync, useToast } from '../components/ui';
import { api, ApiKey, Domain } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

const SCOPES: [string, string][] = [
  ['emails:send', 'Отправка писем'],
  ['emails:read', 'Чтение писем и статусов'],
  ['suppressions:write', 'Изменение подавлений'],
  ['suppressions:read', 'Чтение подавлений'],
  ['contacts:read', 'Чтение контактов, событий и сегментов'],
  ['contacts:write', 'Изменение контактов и сегментов, удаление по GDPR'],
  ['events:write', 'Передача событий'],
  ['journeys:read', 'Чтение сценариев, статистики и отчётов; симуляции нужен и contacts:read'],
  ['journeys:write', 'Сценарии как код: версии, запрос согласования, публикация согласованного, пауза, зачисление'],
];

export function ApiKeysPage() {
  const { me } = useSession();
  const toast = useToast();
  const keys = useAsync(() => api.get<ApiKey[]>('/api-keys'));
  const domains = useAsync(() => api.get<Domain[]>('/domains'));
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState<ApiKey | null>(null);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [scopes, setScopes] = useState(SCOPES.map(([s]) => s));
  const verified = domains.data?.find((d) => d.status === 'verified');

  async function create() {
    const k = await api.post<ApiKey>('/api-keys', { name, mode, scopes });
    setCreated(k);
    setOpen(false);
    setName('');
    keys.reload();
  }
  async function revoke(k: ApiKey) {
    if (!confirm(t('Отозвать ключ «{name}»? Запросы с ним сразу начнут получать 401.', { name: k.name }))) return;
    await api.del(`/api-keys/${k.id}`);
    toast(t('Ключ отозван'));
    keys.reload();
  }

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('API-ключи')}</h1>
          <p>{t('Ключи для серверов. Храним только хеш: потерянный ключ не восстановить — отзовите и создайте новый.')}</p>
        </div>
        {canWrite(me) && <button className="btn btn-primary" onClick={() => setOpen(true)} data-track="api_key.create_click"><IconPlus size={16} /> {t('Новый ключ')}</button>}
      </div>

      {created?.key && (
        <div className="card card-pad stack" style={{ marginBottom: 16 }}>
          <h3>{t('Ключ «{name}» создан', { name: created.name })}</h3>
          <div className="secret-box"><code>{created.key}</code><CopyButton value={created.key} track="api_key.copy" /></div>
          <div className="callout warn">{t('Скопируйте ключ сейчас — после обновления страницы он больше не будет показан.')}</div>
          <CodeTabs snippets={sendSnippets(created.key, verified ? `hello@${verified.name}` : t('hello@ваш-домен.com'))} />
          <div><button className="btn btn-sm" onClick={() => setCreated(null)}>{t('Готово, я сохранил')}</button></div>
        </div>
      )}

      <div className="card">
        {keys.loading ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : !keys.data?.length ? (
          <Empty icon={<IconKey size={36} />} title={t('Ключей пока нет')} action={<button className="btn btn-primary" onClick={() => setOpen(true)}>{t('Создать ключ')}</button>}>
            {t('Создайте ключ, чтобы отправлять письма из своего кода.')}
          </Empty>
        ) : (
          <table className="table">
            <thead>
              <tr><th>{t('Название')}</th><th>{t('Ключ')}</th><th>{t('Режим')}</th><th>{t('Права')}</th><th>{t('Создан')}</th><th>{t('Использован')}</th><th /></tr>
            </thead>
            <tbody>
              {keys.data.map((k) => (
                <tr key={k.id} style={{ opacity: k.revoked_at ? 0.55 : 1 }}>
                  <td><strong>{k.name}</strong></td>
                  <td><code>{k.prefix}…</code></td>
                  <td><span className={`pill ${k.mode === 'live' ? 'sent' : 'queued'}`}>{k.mode}</span></td>
                  <td className="small muted">{k.scopes.length === SCOPES.length ? t('все') : k.scopes.join(', ')}</td>
                  <td className="muted small">{ago(k.created_at)}</td>
                  <td className="muted small">{ago(k.last_used_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {k.revoked_at ? (
                      <span className="pill revoked">{t('отозван')}</span>
                    ) : (
                      canWrite(me) && <button className="btn btn-sm btn-danger" onClick={() => revoke(k)}>{t('Отозвать')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <Modal
          title={t('Новый API-ключ')}
          onClose={() => setOpen(false)}
          footer={
            <>
              <button className="btn" onClick={() => setOpen(false)}>{t('Отмена')}</button>
              <button className="btn btn-primary" onClick={create} disabled={!name || !scopes.length}>{t('Создать')}</button>
            </>
          }
        >
          <Field label={t('Название')}>
            <input className="input" autoFocus placeholder={t('Backend production')} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('Режим')} hint={t('Префикс mo_live_ или mo_test_ помогает не перепутать окружения')}>
            <div className="chips">
              {(['test', 'live'] as const).map((m) => (
                <button key={m} className={`chip${mode === m ? ' on' : ''}`} onClick={() => setMode(m)}>{m === 'test' ? t('Тестовый') : t('Боевой')}</button>
              ))}
            </div>
          </Field>
          <Field label={t('Права')} hint={t('Минимально необходимые права уменьшают ущерб при утечке')}>
            <div className="stack" style={{ gap: 6 }}>
              {SCOPES.map(([s, label]) => (
                <label key={s} className="row" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={scopes.includes(s)} onChange={() => setScopes((xs) => (xs.includes(s) ? xs.filter((x) => x !== s) : [...xs, s]))} />
                  <code>{s}</code> <span className="muted small">{t(label)}</span>
                </label>
              ))}
            </div>
          </Field>
        </Modal>
      )}
    </>
  );
}
